import { NavigationBar } from '../../components/ui/NavigationBar';
import { useTranslations } from '../../i18n';
import { usePreferences } from '../../contexts/PreferencesContext';
import { Check } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import { trackPricingEvent } from '../../lib/activityTracking';
import { FeedbackModal } from '../../components/ui/FeedbackModal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { supabase } from '../../lib/supabase';

const WORKER_URL = 'https://mycarcosts.gtreulands.workers.dev';

type SettingsPlansProps = {
    onBack: () => void;
};

export function SettingsPlans({ onBack }: SettingsPlansProps) {
    const { t } = useTranslations();
    const { preferences, updatePreferences } = usePreferences();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Restore scroll position
    useScrollRestoration(scrollContainerRef);

    const formatPeriodEnd = (ts: any) => {
        if (!ts) return 'N/A';
        const ms = ts > 10000000000 ? ts : ts * 1000;
        const date = new Date(ms);
        return isNaN(date.getTime()) ? `N/A (${ts})` : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    };

    // We assume default plan is 'free'
    const currentPlan = preferences?.plan_type || 'free';
    const isCustomPlan = currentPlan !== 'free' && currentPlan !== 'pro';
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<'pro' | 'custom' | null>(null);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [upgradeError, setUpgradeError] = useState<string | null>(null);
    const hasTrackedOpened = useRef(false);

    // Track 'opened' event on mount
    useEffect(() => {
        if (!hasTrackedOpened.current) {
            trackPricingEvent({ eventType: 'opened' });
            hasTrackedOpened.current = true;
        }
    }, []);

    const [subDetails, setSubDetails] = useState<{
        active: boolean;
        status: string;
        current_period_end: number;
        price: number;
        currency: string;
        cancel_at_period_end: boolean;
    } | null>(null);

    useEffect(() => {
        if (currentPlan === 'pro') {
            const fetchSubDetails = async () => {
                try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) return;

                    const res = await fetch(`${WORKER_URL}/stripe/subscription-details`, {
                        headers: {
                            'Authorization': `Bearer ${session.access_token}`
                        }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data.active) {
                            setSubDetails(data);
                        }
                    }
                } catch (err) {
                    console.error('Failed to fetch subscription details:', err);
                }
            };
            fetchSubDetails();
        }
    }, [currentPlan]);

    const handleUpgradeClick = async (plan: 'pro' | 'custom') => {
        if (plan === 'custom') {
            setSelectedPlan(plan);
            setIsFeedbackModalOpen(true);
            trackPricingEvent({ eventType: 'pressed_custom' });
            return;
        }

        setIsUpgrading(true);
        setUpgradeError(null);
        trackPricingEvent({ 
            eventType: 'pressed_pro',
            billingCycle: 'monthly'
        });

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');

            const response = await fetch(`${WORKER_URL}/stripe/create-checkout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({ origin: window.location.origin })
            });

            if (!response.ok) {
                const errJson = await response.json().catch(() => ({}));
                throw new Error(errJson.error || 'Failed to start checkout session');
            }

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error('No checkout URL returned');
            }
        } catch (err: any) {
            console.error('Upgrade to pro failed:', err);
            setUpgradeError(err.message || 'Error redirecting to Stripe');
            setIsUpgrading(false);
        }
    };

    const [isManagingPortal, setIsManagingPortal] = useState(false);

    const handleManageBilling = async () => {
        setIsManagingPortal(true);
        setUpgradeError(null);

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');

            const response = await fetch(`${WORKER_URL}/stripe/create-portal`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({ origin: window.location.origin })
            });

            if (!response.ok) {
                const errJson = await response.json().catch(() => ({}));
                throw new Error(errJson.error || 'Failed to open billing settings');
            }

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error('No portal URL returned');
            }
        } catch (err: any) {
            console.error('Portal redirect failed:', err);
            setUpgradeError(err.message || 'Error opening billing settings');
            setIsManagingPortal(false);
        }
    };

    const handleDowngrade = () => {
        setIsDowngradeModalOpen(true);
    };

    const confirmDowngrade = async () => {
        setIsDowngradeModalOpen(false);
        try {
            await updatePreferences({ plan_type: 'free' });
            trackPricingEvent({ eventType: 'downgrade_to_free' });
        } catch (err) {
            console.error('Failed to downgrade:', err);
        }
    };

    const handleFeedbackSubmit = (feedback: string) => {
        if (!selectedPlan) return;
        trackPricingEvent({
            eventType: selectedPlan === 'pro' ? 'pressed_pro' : 'pressed_custom',
            billingCycle: selectedPlan === 'pro' ? billingCycle : undefined,
            feedback
        });
    };

    return (
        <div className="fixed inset-0 flex flex-col h-full bg-transparent overflow-hidden">
            <div className="flex-shrink-0 z-30">
                <NavigationBar
                    title={t.settings.plans || 'Plans'}
                    onBack={onBack}
                    fixed={false}
                />
            </div>

            <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto no-scrollbar pt-4 pb-32"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Upgrade Error Banner */}
                    {upgradeError && (
                        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm font-semibold text-center">
                            {upgradeError}
                        </div>
                    )}

                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Free Plan */}
                        <div className={`border rounded-2xl p-8 bg-white dark:bg-slate-900 flex flex-col shadow-sm ${currentPlan === 'free' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-slate-200 dark:border-slate-700'}`}>
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold">{t.landing.pricing.free.title}</h3>
                                <div className="mt-4 flex items-baseline">
                                    <span className="text-4xl font-extrabold tracking-tight">$0</span>
                                    <span className="ml-1 text-xl font-semibold text-slate-500 dark:text-slate-400">/ {t.landing.pricing.perMonth}</span>
                                </div>
                            </div>
                            <ul className="space-y-4 mb-8 flex-1">
                                {[
                                    { text: t.landing.pricing.features.oneCar, included: true },
                                    { text: t.landing.pricing.features.unlimitedExpenses, included: true },
                                    { text: t.landing.pricing.features.unlimitedSpareParts, included: true },
                                    { text: t.landing.pricing.features.unlimitedNotes, included: true },
                                    { text: t.landing.pricing.features.repeatedExpenses, included: true },
                                    { text: t.landing.pricing.features.emailReminders, included: true },
                                    { text: t.landing.pricing.features.basicStatistics, included: true },
                                    { text: t.landing.pricing.features.storage50mb, included: true },
                                    { text: t.landing.pricing.features.unlimitedArchivedCars, included: true },
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start">
                                        <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 ${feature.included ? 'text-green-500 bg-green-50 dark:bg-green-900/20' : 'text-slate-300 dark:text-slate-700 bg-slate-100 dark:bg-slate-800'}`}>
                                            {feature.included ? <Check size={12} strokeWidth={3} /> : <div className="w.1.5 h-1.5 rounded-full bg-current" />}
                                        </div>
                                        <span className={feature.included ? 'text-slate-900 dark:text-slate-200' : 'text-slate-500 dark:text-slate-500'}>{feature.text}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={currentPlan === 'pro' ? handleManageBilling : (currentPlan !== 'free' ? handleDowngrade : undefined)}
                                disabled={currentPlan === 'free' || isManagingPortal}
                                className={`w-full py-4 rounded-xl font-bold transition-all ${currentPlan === 'free'
                                    ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 cursor-default'
                                    : (currentPlan === 'pro'
                                        ? 'bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                        : 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30')}`}
                            >
                                {currentPlan === 'free' 
                                    ? (t.settings.currentlyPlan || 'Currently') 
                                    : (currentPlan === 'pro' 
                                        ? (isManagingPortal ? (t.common.loading || 'Loading...') : (t.settings.manageSubscription || 'Manage Subscription')) 
                                        : ((t.common as any).downgrade || 'Downgrade'))}
                            </button>
                        </div>

                        {/* Pro Plan */}
                        <div className={`border-2 rounded-2xl p-8 bg-blue-50 dark:bg-blue-900/10 flex flex-col relative shadow-md ${currentPlan === 'pro' ? 'border-green-500 ring-4 ring-green-500/20' : 'border-blue-600 dark:border-blue-500'}`}>
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold">{t.landing.pricing.pro.title}</h3>
                                <div className="mt-4 flex items-baseline">
                                    <span className="text-4xl font-extrabold tracking-tight">$4.99</span>
                                    <span className="ml-1 text-xl font-semibold text-slate-500 dark:text-slate-400">/ {t.landing.pricing.perMonth}</span>
                                </div>
                                {subDetails && (
                                    <div className="mt-3 text-xs text-blue-600 dark:text-blue-400 font-bold bg-blue-500/10 px-3 py-2 rounded-xl inline-block border border-blue-500/20">
                                        {subDetails.cancel_at_period_end ? (
                                            <span>
                                                {t.settings.endsOn || 'Ends on'}: {formatPeriodEnd(subDetails.current_period_end)}
                                            </span>
                                        ) : (
                                            <span>
                                                {t.settings.nextPayment || 'Next payment'}: {formatPeriodEnd(subDetails.current_period_end)} ({new Intl.NumberFormat(undefined, { style: 'currency', currency: subDetails.currency.toUpperCase() }).format(subDetails.price)})
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <ul className="space-y-4 mb-8 flex-1">
                                {[
                                    { text: t.landing.pricing.features.allInFreePlus, included: true, isHeader: true },
                                    { text: t.landing.pricing.features.threeCars, included: true },
                                    { text: t.landing.pricing.features.storage2gb.replace('2 Gb', '1 Gb').replace('2 Go', '1 Go').replace('2 Гб', '1 Гб'), included: true },
                                    { text: t.landing.pricing.features.emailParsing, included: true },
                                    { text: t.landing.pricing.features.carAdmins, included: true },
                                    { text: t.landing.pricing.features.fullStatistics, included: true },
                                    { text: t.landing.pricing.features.dataExport, included: true },
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start">
                                        {feature.isHeader ? (
                                            <span className="text-xs font-black uppercase tracking-widest text-blue-600 dark:text-blue-400 mt-1 mb-1">{feature.text}</span>
                                        ) : (
                                            <>
                                                <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 text-blue-600 bg-blue-100 dark:bg-blue-900/30">
                                                    <Check size={12} strokeWidth={3} />
                                                </div>
                                                <span className="text-slate-900 dark:text-slate-200 font-medium">{feature.text}</span>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={currentPlan === 'pro' ? handleManageBilling : (isCustomPlan ? handleDowngrade : () => handleUpgradeClick('pro'))}
                                disabled={isUpgrading || isManagingPortal}
                                className={`w-full py-4 rounded-xl font-bold transition-all shadow-xl shadow-blue-500/20 ${currentPlan === 'pro'
                                    ? 'bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                    : (currentPlan === 'free' 
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50' 
                                        : 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 border border-red-100 dark:border-red-900/30 shadow-none pointer-events-auto opacity-100')}`}
                            >
                                {currentPlan === 'pro' 
                                    ? (isManagingPortal ? (t.common.loading || 'Loading...') : (t.settings.manageSubscription || 'Manage Subscription')) 
                                    : (isUpgrading 
                                        ? (t.common.loading || 'Loading...') 
                                        : (currentPlan === 'free' ? (t.settings.upgradePlan || 'Upgrade') : ((t.common as any).downgrade || 'Downgrade')))}
                            </button>
                        </div>

                        {/* Custom Plan */}
                        <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-8 bg-white dark:bg-slate-900 flex flex-col shadow-sm">
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold">{t.landing.pricing.custom.title}</h3>
                            </div>
                            <ul className="space-y-4 mb-8 flex-1">
                                {[
                                    { text: t.landing.pricing.features.moreCars, included: true },
                                    { text: t.landing.pricing.features.moreFileStorage, included: true },
                                    { text: t.landing.pricing.features.customRequirements, included: true },
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-start">
                                        <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 text-blue-600 bg-blue-50 dark:bg-blue-900/20">
                                            <Check size={12} strokeWidth={3} />
                                        </div>
                                        <span className="text-slate-900 dark:text-slate-200">{feature.text}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => handleUpgradeClick('custom')}
                                className="w-full py-4 bg-slate-900 dark:bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-800 dark:hover:bg-slate-700 transition-all"
                            >
                                {t.landing.pricing.custom.cta || t.settings.contactUs}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <FeedbackModal
                isOpen={isFeedbackModalOpen}
                onClose={() => {
                    setIsFeedbackModalOpen(false);
                    setSelectedPlan(null);
                }}
                onSubmit={handleFeedbackSubmit}
            />
            <ConfirmModal
                isOpen={isDowngradeModalOpen}
                title={(t.common as any).downgrade || 'Downgrade'}
                message={(t.common as any).downgradeConfirm || 'Are you sure you want to downgrade to Free plan?'}
                onConfirm={confirmDowngrade}
                onCancel={() => setIsDowngradeModalOpen(false)}
                confirmLabel={(t.common as any).downgrade || 'Downgrade'}
                cancelLabel={t.common.cancel || 'Cancel'}
                isDestructive={true}
            />
        </div >
    );
}
